// src/api/features/locations/queries.ts
import {
  useQuery,
  type QueryClient,
  type QueryKey,
  keepPreviousData,
} from "@tanstack/react-query";

import {
  listCountries,
  listRegions,
  listCities,
  listCityAliases,
  getCountry,
  getRegion,
  getCity,
} from "./api";

import type {
  PaginatedCountries,
  PaginatedRegions,
  PaginatedCities,
  PaginatedCityAliases,
  ListCountriesQuery,
  ListRegionsQuery,
  ListCitiesQuery,
  ListCityAliasesQuery,
  LocationCountry,
  LocationRegion,
  LocationCity,
} from "./types";

/* =========================
 * Query Keys (same style as LeadsKeys)
 * ========================= */

export const LocationsKeys = {
  all: ["locations"] as const,

  countries: (params: ListCountriesQuery = {}) =>
    ["locations", "countries", params] as const,

  regions: (params: ListRegionsQuery) =>
    ["locations", "regions", params] as const,

  cities: (params: ListCitiesQuery) =>
    ["locations", "cities", params] as const,

  cityAliases: (params: ListCityAliasesQuery = {}) =>
    ["locations", "city-aliases", params] as const,

  country: (iso2: string) => ["locations", "country", iso2] as const,
  region: (id: number) => ["locations", "region", id] as const,
  city: (id: number) => ["locations", "city", id] as const,
} as const;

/* =========================
 * Single record hooks (admin edit screens)
 * Each edit page loads its own record from the URL, so a deep link or a
 * page refresh works without a list being rendered first.
 * ========================= */

export function useCountry(iso2: string | null | undefined) {
  const key = String(iso2 ?? "").trim().toUpperCase();
  const enabled = key.length === 2;

  return useQuery<LocationCountry>({
    queryKey: LocationsKeys.country(key),
    queryFn: () => getCountry(key),
    enabled,
    staleTime: 60_000,
  });
}

export function useRegion(id: number | null | undefined) {
  const enabled = Number.isFinite(Number(id)) && Number(id) > 0;

  return useQuery<LocationRegion>({
    queryKey: LocationsKeys.region(Number(id)),
    queryFn: () => getRegion(Number(id)),
    enabled,
    staleTime: 60_000,
  });
}

export function useCity(id: number | null | undefined) {
  const enabled = Number.isFinite(Number(id)) && Number(id) > 0;

  return useQuery<LocationCity>({
    queryKey: LocationsKeys.city(Number(id)),
    queryFn: () => getCity(Number(id)),
    enabled,
    staleTime: 60_000,
  });
}

/* =========================
 * COUNTRIES — React Query Hooks
 * (GET /locations/countries)
 * ========================= */

export function useCountries(params: ListCountriesQuery = {}) {
  const key = LocationsKeys.countries(params);

  return useQuery<PaginatedCountries>({
    queryKey: key,
    queryFn: () => listCountries(params),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

/* =========================
 * REGIONS — React Query Hooks
 * (GET /locations/regions)
 * ========================= */

export function useRegions(params: ListRegionsQuery) {
  const enabled = Boolean(params?.countryIso2);
  const key = enabled
    ? LocationsKeys.regions(params)
    : (["locations", "regions", "disabled"] as QueryKey);

  return useQuery<PaginatedRegions>({
    queryKey: key,
    queryFn: () => listRegions(params),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

/* =========================
 * CITIES — React Query Hooks
 * (GET /locations/cities)
 * ========================= */

export function useCities(params: ListCitiesQuery) {
  const enabled = Boolean(params?.countryIso2);
  const key = enabled
    ? LocationsKeys.cities(params)
    : (["locations", "cities", "disabled"] as QueryKey);

  return useQuery<PaginatedCities>({
    queryKey: key,
    queryFn: () => listCities(params),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 2 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/* =========================
 * CITY ALIASES — React Query Hooks (ADMIN)
 * (GET /locations/city-aliases)
 * ========================= */

export function useCityAliases(params: ListCityAliasesQuery = {}) {
  // This is an admin-only endpoint (requireAuth), but the hook can exist anywhere.
  // If you want to hard-disable it for non-admin screens, do it at the call site.
  const key = LocationsKeys.cityAliases(params);

  return useQuery<PaginatedCityAliases>({
    queryKey: key,
    queryFn: () => listCityAliases(params),
    placeholderData: keepPreviousData,
    staleTime: 2 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/* =========================
 * Prefetch helpers (optional, mirrors Leads)
 * ========================= */

export function prefetchCountries(qc: QueryClient, params: ListCountriesQuery = {}) {
  return qc.prefetchQuery({
    queryKey: LocationsKeys.countries(params),
    queryFn: () => listCountries(params),
    staleTime: 5 * 60_000,
  });
}

export function prefetchRegions(qc: QueryClient, params: ListRegionsQuery) {
  return qc.prefetchQuery({
    queryKey: LocationsKeys.regions(params),
    queryFn: () => listRegions(params),
    staleTime: 5 * 60_000,
  });
}

export function prefetchCities(qc: QueryClient, params: ListCitiesQuery) {
  return qc.prefetchQuery({
    queryKey: LocationsKeys.cities(params),
    queryFn: () => listCities(params),
    staleTime: 2 * 60_000,
  });
}

export function prefetchCityAliases(
  qc: QueryClient,
  params: ListCityAliasesQuery = {}
) {
  return qc.prefetchQuery({
    queryKey: LocationsKeys.cityAliases(params),
    queryFn: () => listCityAliases(params),
    staleTime: 2 * 60_000,
  });
}
